import { ReviewRow } from './WizardReviewRow';
import { SessionSizeSummary } from './SessionSizeSummary';

/**
 * The wizard's final step — ROUTE_INVENTORY §2.11 #38 and #41.
 *
 * Every row carries its own Edit, which jumps back to the step that owns it.
 * That is what makes Review a summary rather than a dead end: the alternative
 * is a Back button pressed three times while counting steps.
 *
 * The session-size block answers "what am I actually starting?" before the
 * commit, and the cap caveat under it is the only place the wizard admits that
 * more concepts matched than will be practiced.
 */
export function ReviewStep({
  domain,
  user,
  lessons,
  length,
  lessonTitleById,
  domainCountByTag,
  userCountByTag,
  onEditDomain,
  onEditUser,
  onEditLessons,
  onEditLength,
  showLessonsRow,
  effectiveSessionSize,
  isCapped,
  isLoading,
}: {
  domain: string[];
  user: string[];
  lessons: string[];
  length: number | 'all';
  lessonTitleById: Map<string, string>;
  domainCountByTag: Map<string, number>;
  userCountByTag: Map<string, number>;
  onEditDomain: () => void;
  onEditUser: () => void;
  onEditLessons: () => void;
  onEditLength: () => void;
  showLessonsRow: boolean;
  effectiveSessionSize: number | null;
  isCapped: boolean;
  isLoading: boolean;
}) {
  // Sort selected tags by their concept-count (desc) so the most-
  // prominent picks lead the chip row, regardless of click order.
  const sortByCount = (counts: Map<string, number>) => (a: string, b: string) =>
    (counts.get(b) ?? 0) - (counts.get(a) ?? 0);
  const domainSorted = [...domain].sort(sortByCount(domainCountByTag));
  const userSorted = [...user].sort(sortByCount(userCountByTag));
  return (
    <div className='space-y-4'>
      <div>
        <h3 className='text-base font-semibold text-foreground'>
          Review your session
        </h3>
        <p className='mt-1 text-xs text-muted-foreground'>
          Confirm the filters below, then start.
        </p>
      </div>

      <SessionSizeSummary
        effectiveSessionSize={effectiveSessionSize}
        length={length}
        isCapped={isCapped}
        isLoading={isLoading}
      />
      <dl className='divide-y divide-border rounded-lg border border-border'>
        <ReviewRow
          label='Domain'
          empty={domain.length === 0 ? 'Every domain' : null}
          onEdit={onEditDomain}
        >
          {domain.length > 0 && (
            <div className='flex flex-wrap gap-1.5'>
              {domainSorted.map(t => (
                <span
                  key={t}
                  className='inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground dark:bg-white/[0.06]'
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </ReviewRow>
        <ReviewRow
          label='Your tags'
          empty={user.length === 0 ? 'No tag filter' : null}
          onEdit={onEditUser}
        >
          {user.length > 0 && (
            <div className='flex flex-wrap gap-1.5'>
              {userSorted.map(t => (
                <span
                  key={t}
                  className='dark:tint-brand inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:border-primary/60'
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </ReviewRow>
        {showLessonsRow && (
          <ReviewRow
            label='Source lesson'
            empty={lessons.length === 0 ? 'Every lesson' : null}
            onEdit={onEditLessons}
          >
            {lessons.length > 0 && (
              <ul className='space-y-1'>
                {lessons.map(id => (
                  <li key={id} className='text-sm text-foreground'>
                    · {lessonTitleById.get(id) ?? id}
                  </li>
                ))}
              </ul>
            )}
          </ReviewRow>
        )}
        <ReviewRow label='Length' empty={null} onEdit={onEditLength}>
          <span className='text-sm font-medium text-foreground'>
            {length === 'all' ? 'No limit' : `${length} concepts`}
          </span>
        </ReviewRow>
      </dl>
    </div>
  );
}
